import { APISpec } from './api-spec.interface';

/**
 * Basic validation of OpenAPI spec structure
 * @param spec - The spec to validate
 */
export function validateSpec(spec: APISpec): void {
  if (!spec) {
    throw new Error('Specification is empty');
  }

  // Check for OpenAPI 3.0 or Swagger 2.0
  if (!spec.openapi && !spec.swagger) {
    throw new Error('Not a valid OpenAPI/Swagger specification');
  }

  if (!spec.info?.title || !spec.info?.version) {
    throw new Error('Missing required info section');
  }

  if (!spec.paths || Object.keys(spec.paths).length === 0) {
    throw new Error('No paths defined in specification');
  }
}

/**
 * Resolves all local $ref references in the specification.
 * The resolution cache lives for a single call, so repeated
 * parses never see stale references.
 *
 * @param spec - The specification to resolve; not mutated
 * @returns A deep copy of the spec with references expanded
 */
export function resolveSpecRefs(spec: APISpec): APISpec {
  const resolvedRefs = new Map<string, unknown>();

  function isRefObject(value: unknown): value is { $ref: string } {
    return (
      typeof value === 'object' &&
      value !== null &&
      typeof (value as { $ref?: unknown }).$ref === 'string'
    );
  }

  function resolveRef(ref: string, root: unknown, shouldNotExpand: boolean): unknown {
    if (resolvedRefs.has(ref)) {
      return resolvedRefs.get(ref);
    }

    // Only handle local references for now
    if (!ref.startsWith('#/')) {
      console.warn(`External reference not supported: ${ref}`);
      return { $ref: ref };
    }

    // Navigate to the reference
    const path = ref.substring(2).split('/');
    let current = root as Record<string, unknown>;

    for (const segment of path) {
      if (!current[segment]) {
        throw new Error(`Could not resolve reference: ${ref}`);
      }
      current = current[segment] as Record<string, unknown>;
    }

    // Clone to avoid circular references
    const resolved = JSON.parse(JSON.stringify(current));

    if (shouldNotExpand) {
      resolvedRefs.set(ref, resolved);
      return resolved;
    }

    const resolvedDeep = resolveAllRefs(resolved, root, ref);
    resolvedRefs.set(ref, resolvedDeep);
    return resolvedDeep;
  }

  function resolveAllRefs(obj: unknown, root: unknown, mainRef?: string): unknown {
    if (obj === null || obj === undefined) {
      return obj;
    }

    if (isRefObject(obj)) {
      return resolveRef(obj.$ref, root, obj.$ref === mainRef);
    }

    if (Array.isArray(obj)) {
      return obj.map(item => resolveAllRefs(item, root, mainRef));
    }

    if (typeof obj === 'object') {
      const resolved: Record<string, unknown> = {};

      for (const [key, value] of Object.entries(obj)) {
        resolved[key] = resolveAllRefs(value, root, mainRef);
      }

      return resolved;
    }

    return obj;
  }

  const specCopy = JSON.parse(JSON.stringify(spec)) as APISpec;
  return resolveAllRefs(specCopy, specCopy) as APISpec;
}
